// studioShellViewModel.js — m3-13 Agent Studio chat SHELL logic (pure, node-testable).
//
// SHELL ONLY: chat-history sidebar shaping, conversation turn shaping, composer validity,
// model-picker options + persistence. NO streaming, NO real model routing — that's m3-14
// (deferred, chair-gated, blocked on m3-01). This module produces the static view model the
// chat layout renders; the actual agent reply path is a no-op placeholder here.
//
// Model lineup is the CURRENT Claude lineup (claude-api skill, cached 2026-06): Opus 4.8 /
// Sonnet 4.6 / Haiku 4.5. LABELS are the bare family names ("Opus"/"Sonnet"/"Haiku") — version
// numbers never render in the agent experience (item #4, 2026-07-28); model_id keeps the full
// canonical versioned id. Default selection = Sonnet (loop doctrine + DESIGN-INTEGRATION
// §2.2: "Default selection = Sonnet"). Fable 5 is intentionally omitted from the picker (the
// prototype omitted it; routing the most-capable model from a chat picker is out of V1 scope).
//
// MODEL-2 SYNC NOTE: model_id values here are canonical full ids that MUST stay aligned with:
//   - Rails AgentModelCatalog (agent_model_catalog.rb): the fail-closed server allowlist
//   - TS agent-service (src/index.ts): the isAllowedModel allowlist
// Canonical ids: Opus=claude-opus-4-8, Sonnet=claude-sonnet-4-6,
//   Haiku=claude-haiku-4-5-20251001 (dated form — not the short alias claude-haiku-4-5).

export const MODELS = Object.freeze([
  { id: "opus",   model_id: "claude-opus-4-8",          label: "Opus",   blurb: "Most capable — deep reasoning, long tasks" },
  { id: "sonnet", model_id: "claude-sonnet-4-6",        label: "Sonnet", blurb: "Balanced speed and intelligence (default)" },
  { id: "haiku",  model_id: "claude-haiku-4-5-20251001", label: "Haiku",  blurb: "Fastest, most cost-effective" },
])
export const DEFAULT_MODEL = "sonnet"
export const MODEL_STORAGE_KEY = "vesta-studio-model"

// ── UI-1 Effort selector ────────────────────────────────────────────────────────
// EFFORTS is the full enum; availableEfforts gates per model to mirror server policy.
// Server (EFFORT-2) is authoritative — this is a UX guard, not a safety fence.
// DEFAULT: medium (cost guardrail — do NOT default to xhigh/opus).
export const EFFORTS = Object.freeze(["low", "medium", "high", "xhigh", "max"])
export const DEFAULT_EFFORT = "medium"
export const EFFORT_STORAGE_KEY = "vesta-studio-effort"

// Per-model effort gating (UX mirror of server allowlist):
//   haiku  → no effort picker (returns []; effort omitted from send body)
//   sonnet → low/medium/high/max (xhigh excluded — SDK silently downgrades)
//   opus   → all five
export function availableEfforts(modelShortId) {
  if (modelShortId === "haiku") return []
  if (modelShortId === "sonnet") return ["low", "medium", "high", "max"]
  // opus and any unrecognised model → full list
  return [...EFFORTS]
}

export function isValidEffort(effort, modelShortId) {
  return availableEfforts(modelShortId).includes(effort)
}

// DESIGN-PANEL repair (A3 pass) — display-only capitalization for an effort value ("high" ->
// "High"). Purely cosmetic: callers still read/write the lowercase enum untouched (NewAgentForm's
// <select> value/onChange, buildSendBody/buildAgentJobPayload) — this only maps what's shown in
// an <option>'s text.
export function capitalizeEffort(ef) {
  return typeof ef === "string" && ef.length > 0 ? ef.charAt(0).toUpperCase() + ef.slice(1) : ef
}

export function persistEffort(effort, storage) {
  if (!storage) return false
  try { storage.setItem(EFFORT_STORAGE_KEY, effort); return true } catch (_e) { return false }
}

export function readPersistedEffort(storage) {
  if (!storage) return null
  try { return storage.getItem(EFFORT_STORAGE_KEY) } catch (_e) { return null }
}

// Resolve the right effort given a requested value, a stored value, and the current model.
// Falls back to DEFAULT_EFFORT if neither is valid for the model.
export function resolveEffort(requested, stored, modelShortId) {
  if (isValidEffort(requested, modelShortId)) return requested
  if (isValidEffort(stored, modelShortId)) return stored
  // For haiku, DEFAULT_EFFORT is fine as the internal state even though it won't be sent.
  return DEFAULT_EFFORT
}

// Build the JSON body sent with createChat / postMessage.
// Effort is included only when the model's availableEfforts is non-empty.
// Task 5: attachments (already mapped to the wire shape {url, mime_type, name?, size?}) are
// included ONLY when non-empty, so a no-attachment send body stays byte-identical to today.
export function buildSendBody({ text, message_id, model, effort, attachments }) {
  const base = { text, model }
  if (message_id !== undefined) base.message_id = message_id
  if (availableEfforts(model).length > 0) base.effort = effort
  if (Array.isArray(attachments) && attachments.length > 0) base.attachments = attachments
  return base
}

// ── Task 5: composer attachments (plus-button + drag-drop) ──────────────────────
// The allowlist mirrors the Rails AttachmentParams + the TS dispatch guard (defense in depth on
// the client is UX, not the security fence — the server re-validates every field).
export const ATTACHMENT_IMAGE_MIME_TYPES = Object.freeze(["image/png", "image/jpeg", "image/webp"])
export const ATTACHMENT_MIME_TYPES = Object.freeze([...ATTACHMENT_IMAGE_MIME_TYPES, "application/pdf"])
export const MAX_ATTACHMENTS = 4
export const ATTACHMENT_IMAGE_MAX_BYTES = 10 * 1024 * 1024
export const ATTACHMENT_PDF_MAX_BYTES = 20 * 1024 * 1024
export const ATTACHMENT_ACCEPT = ATTACHMENT_MIME_TYPES.join(",")

// Client-side validation for one picked/dropped file, given how many are already attached.
// Pure → node-testable. Returns {ok:true} or {ok:false, reason}. The reason renders as a transient
// .st-att-error line in the chip area (auto-clears). mime allowlist + per-type size cap + count ≤ 4.
export function validateAttachmentFile(file, currentCount = 0) {
  if (currentCount >= MAX_ATTACHMENTS) {
    return { ok: false, reason: `You can attach up to ${MAX_ATTACHMENTS} files.` }
  }
  const type = (file && file.type) || ""
  if (!ATTACHMENT_MIME_TYPES.includes(type)) {
    return { ok: false, reason: `${(file && file.name) || "That file"} isn't a supported type (PNG, JPEG, WebP, or PDF).` }
  }
  const size = (file && file.size) || 0
  const isImage = ATTACHMENT_IMAGE_MIME_TYPES.includes(type)
  const cap = isImage ? ATTACHMENT_IMAGE_MAX_BYTES : ATTACHMENT_PDF_MAX_BYTES
  if (size > cap) {
    return { ok: false, reason: `${(file && file.name) || "That file"} is too large (max ${Math.round(cap / 1024 / 1024)}MB).` }
  }
  return { ok: true }
}

// True for the image mime types (drives thumbnail vs doc-glyph rendering of a chip).
export function isImageAttachment(mimeType) {
  return ATTACHMENT_IMAGE_MIME_TYPES.includes(mimeType)
}

// Map the composer's attachment state objects ({id, name, mimeType, size, url, status}) to the
// wire shape ({url, mime_type, name, size}) — ONLY the done-status ones with a url. Pure.
export function attachmentsToWire(attachments = []) {
  return (attachments || [])
    .filter((a) => a && a.status === "done" && a.url)
    .map((a) => ({ url: a.url, mime_type: a.mimeType, name: a.name, size: a.size }))
}

// True while any attachment is still uploading — the composer disables Send until all resolve.
export function hasUploadingAttachment(attachments = []) {
  return (attachments || []).some((a) => a && a.status === "uploading")
}

export function isValidModel(id) {
  return MODELS.some((m) => m.id === id)
}

export function resolveModel(requested, stored) {
  if (isValidModel(requested)) return requested
  if (isValidModel(stored)) return stored
  return DEFAULT_MODEL
}

export function modelById(id) {
  return MODELS.find((m) => m.id === id) || MODELS.find((m) => m.id === DEFAULT_MODEL)
}

export function persistModel(id, storage) {
  if (!storage || !isValidModel(id)) return false
  try { storage.setItem(MODEL_STORAGE_KEY, id); return true } catch (_e) { return false }
}

export function readPersistedModel(storage) {
  if (!storage) return null
  try { return storage.getItem(MODEL_STORAGE_KEY) } catch (_e) { return null }
}

// ── Composer validity — Enter sends, Shift+Enter newlines; send disabled when empty. ──
export function canSend(input) {
  return typeof input === "string" && input.trim().length > 0
}

// Decide what a key event in the composer means (pure — the component wires the handler).
// Returns 'send' | 'newline' | 'noop'.
export function composerKeyAction({ key, shiftKey }, input) {
  if (key !== "Enter") return "noop"
  if (shiftKey) return "newline"
  return canSend(input) ? "send" : "noop"
}

// ── S-E5 send gating (P0-1 send-while-streaming / P0-2 double-send) ─────────────
// canSubmit is the AUTHORITATIVE composer-enable predicate. Unlike canSend (input-only),
// it ALSO gates on the run lifecycle: a send is allowed ONLY when the input is non-empty
// AND nothing is in flight (`sending`) AND the agent is not currently replying (`working`).
// Gating on `working` (not just `sending`) is the fix: `sending` clears at HTTP 201 while the
// reply is still streaming, so a `!sending`-only gate re-enables Send DURING streaming → a
// second send 409s into a swallowed catch → ghost turn. `working` stays true for the whole reply.
export function canSubmit({ input, sending, working } = {}) {
  if (working) return false
  if (sending) return false
  return canSend(input)
}

// Synchronous same-tick send latch (P0-2). The composer holds a ref (e.g. useRef(false)).
// acquireSendLock claims it: returns false if already locked (a second Enter in the same tick
// is dropped before it can mint a second UUID / POST), else sets it and returns true.
// releaseSendLock clears it (call in a finally). Pure over a {current} ref so it's node-testable.
export function acquireSendLock(ref) {
  if (!ref) return false
  if (ref.current) return false
  ref.current = true
  return true
}

export function releaseSendLock(ref) {
  if (ref) ref.current = false
}

// ── Chat-history sidebar shaping ──────────────────────────────────────────────
// Relative age string ("2m", "1h", "3d"); active thread shows no age (design docs/03).
export function relativeAge(then, now = Date.now()) {
  const ms = Math.max(0, now - then)
  const min = Math.floor(ms / 60000)
  if (min < 1) return "now"
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const d = Math.floor(hr / 24)
  return `${d}d`
}

// Build the chat-list view model. `chats` = [{id, title, updatedAt, messages?}], `activeId`,
// optional `search` (matches title + message text — the prototype does both). Active thread
// is highlighted and shows no age.
export function buildChatList(chats, activeId, search = "", now = Date.now()) {
  const q = (search || "").trim().toLowerCase()
  const rows = (chats || [])
    .filter((c) => {
      if (!q) return true
      if ((c.title || "").toLowerCase().includes(q)) return true
      return (c.messages || []).some((m) => (m.text || "").toLowerCase().includes(q))
    })
    .map((c) => ({
      id: c.id,
      title: c.title || "Untitled",
      active: c.id === activeId,
      age: c.id === activeId ? "" : relativeAge(c.updatedAt || now, now),
    }))
  return {
    rows,
    empty: rows.length === 0,
    emptyLabel: q ? `No chats match "${search}".` : "No chats yet.",
  }
}

// ── Conversation turn shaping ─────────────────────────────────────────────────
// A turn is {role: 'user'|'assistant', text, tool?}. The shell renders the hero empty state
// when there are no turns. (m3-14 supplies real streamed turns; this just shapes them.)
export function buildConversation(messages) {
  const turns = messages || []
  return {
    turns,
    isEmpty: turns.length === 0,
    heroTitle: "Let's get some work done!",
    heroLead: "I can:",
    heroPoints: [
      "Answer questions about your Vesta account",
      "Complete tasks right now",
      "Create Agent Jobs that handle recurring work automatically",
    ],
  }
}

// TASK 9: the empty-state suggestion CARDS constant (CHAT_SUGGESTIONS) has been RETIRED. The dock's
// quick-task + agent-job suggestions now derive from the single checked-in library registry via
// libraryViewModel/dockSuggestionsViewModel (one honest catalog powering the dock, the Agent Jobs
// slider, and the library page). The two HONEST cards' safety copy was migrated verbatim into the
// registry (draft-meta-campaign keeps the ask-budget-first + "starts paused" wording; review-calendar
// keeps its per-publish-approval wording) — same in-repo review bar, just a different backing file.
